import { useEffect, useRef } from 'react'
import { useTheme } from '../context/ThemeContext.js'
import { usePageMeta } from '../hooks/ui/usePageMeta.js'
import { useFixerHeroScrollParallax } from '../hooks/ui/useFixerHeroScrollParallax.js'
import { useHeroTilt } from '../hooks/ui/useHeroTilt.js'
import { AiAgencyContactSection } from './AiAgencyContactSection.js'
import { AiAgencyFaqSection } from './AiAgencyFaqSection.js'
import { AiAgencyHeroHighlights } from './AiAgencyHeroHighlights.js'
import { FixerAsciiRainLayer } from './FixerAsciiRain.js'
import { FixerHeroVisual } from './FixerHeroVisual.js'
import { FixerMobileNav } from './FixerMobileNav.js'
import {
  FixerBulletList,
  FixerCard,
  FixerCtaRow,
  FixerKicker,
  FixerSection,
  FixerSectionIntro,
} from './fixer-ui.js'
import { FutureDotButton, FutureDotMatrixIcon } from './future-chat/FutureDotPrimitives.js'
import { useAiAgencyContent } from '../hooks/i18n/usePageContent.js'
import { LocaleToggle } from './ui/LocaleToggle.js'
import {
  buildAiAgencyStructuredData,
  PAGE_OG_IMAGE,
  PAGE_URL,
  phone,
  phoneTel,
  bookingUrl,
} from '../data/ai-agency-content.js'
import './future-chat/future-chat-theme.css'
import './fixer-page.css'
import './ai-agency-page.css'

interface AiAgencyPageProps {
  onBack: () => void
  onStartChat?: () => void
}

function CtaArrow() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function AiAgencyPage({ onBack, onStartChat }: AiAgencyPageProps) {
  const c = useAiAgencyContent()
  const { isDarkMode, toggleTheme } = useTheme()
  const theme = isDarkMode ? 'dark' : 'light'

  const scrollRootRef = useRef<HTMLDivElement>(null)
  const heroPanelRef = useRef<HTMLDivElement>(null)

  const {
    stageStyle,
    imageStyle,
    onPointerMove,
    onPointerLeave,
    reducedMotion: tiltReducedMotion,
  } = useHeroTilt({
    maxDeg: 4,
    parallaxPx: 20,
    containerRef: heroPanelRef,
  })

  const { scrollLayerStyle, reducedMotion: scrollReducedMotion } = useFixerHeroScrollParallax(
    scrollRootRef,
    heroPanelRef
  )

  const reducedMotion = tiltReducedMotion || scrollReducedMotion

  const year = new Date().getFullYear()

  usePageMeta(c.meta.title, c.meta.description, PAGE_URL, {
    ogTitle: c.meta.title,
    ogDesc: c.meta.description,
    ogImage: PAGE_OG_IMAGE,
    ogUrl: PAGE_URL,
    keywords: c.meta.keywords,
  })

  useEffect(() => {
    const existing = document.getElementById('ai-agency-structured-data')
    if (existing) existing.remove()

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'ai-agency-structured-data'
    script.textContent = JSON.stringify(buildAiAgencyStructuredData())
    document.head.appendChild(script)

    return () => {
      const s = document.getElementById('ai-agency-structured-data')
      if (s) s.remove()
    }
  }, [])

  return (
    <div
      data-future-chat-theme={theme}
      className="future-chat-shell fixer-page-shell agency-page-shell h-[100dvh] overflow-hidden"
    >
      <FixerAsciiRainLayer
        scrollRootRef={scrollRootRef}
        heroPanelRef={heroPanelRef}
        reducedMotion={reducedMotion}
        isDarkMode={isDarkMode}
      />

      <div ref={scrollRootRef} className="fixer-page-scroll custom-scrollbar">
        <header className="fixer-page-header">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" onClick={onBack} className="fixer-btn-ghost hidden sm:inline-flex">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              F.B/c
            </button>
            <span className="future-brand-lockup-text text-base sm:text-lg">{c.hero.brandLockup}</span>
          </div>

          <nav className="fixer-page-header-nav" aria-label={c.chrome.sectionsAria}>
            {c.nav.headerLinks.map(link => (
              <a key={link.href} href={link.href}>{link.label}</a>
            ))}
          </nav>

          <div className="fixer-page-header-actions flex shrink-0 items-center gap-1">
            <FixerMobileNav
              links={c.nav.links}
              ctaHref={bookingUrl}
              ctaLabel={c.hero.primaryCta}
            />
            <LocaleToggle className="future-locale-toggle fixer-locale-toggle" />
            <FutureDotButton
              glyph={theme === 'dark' ? 'sun' : 'moon'}
              label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              onClick={toggleTheme}
            />
            <button
              type="button"
              onClick={onStartChat}
              className="fixer-btn-primary !py-2 !px-4 text-[11px] hidden sm:inline-flex"
            >
              {c.hero.chatCta}
              <CtaArrow />
            </button>
          </div>
        </header>

        <main>
          <section
            ref={heroPanelRef}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            className="fixer-hero-panel fixer-hero-band"
            aria-labelledby="agency-hero-heading"
          >
            <div className="absolute inset-0">
              <FixerHeroVisual
                stageStyle={stageStyle}
                imageStyle={imageStyle}
                scrollLayerStyle={scrollLayerStyle}
                reducedMotion={reducedMotion}
              />
            </div>

            <div className="fixer-hero-inner">
              <div className="flex flex-1 flex-col gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
                <div className="fixer-hero-copy flex max-w-xl flex-col gap-4">
                  <FixerKicker channel="signal" className="text-white/80">
                    {c.hero.overline}
                  </FixerKicker>
                  <h1
                    id="agency-hero-heading"
                    className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-[3.25rem] lg:leading-[1.08]"
                  >
                    {c.hero.title}
                    <br />
                    <span className="text-white/85">{c.hero.titleAccent}</span>
                  </h1>
                  <p className="fixer-hero-copy-lead max-w-lg text-base leading-relaxed text-white/90 md:text-lg">
                    {c.hero.lead}
                  </p>

                  <FixerCtaRow>
                    <a
                      href={bookingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="fixer-btn-primary w-full sm:w-auto"
                    >
                      {c.hero.primaryCta}
                      <CtaArrow />
                    </a>
                    <button type="button" onClick={onStartChat} className="fixer-btn-secondary w-full sm:w-auto">
                      {c.hero.secondaryCta}
                    </button>
                    <a href={phoneTel} className="fixer-btn-secondary w-full sm:w-auto">
                      {c.hero.callLabel} · {phone}
                    </a>
                  </FixerCtaRow>

                  <p className="flex items-center gap-2 text-sm text-white/65">
                    <FutureDotMatrixIcon glyph="location" state="signal" size={14} className="shrink-0" />
                    {c.hero.locationLine}
                  </p>
                </div>

                <AiAgencyHeroHighlights />
              </div>
            </div>
          </section>

          <FixerSection className="!py-10">
            <div className="fixer-trust-strip">
              {c.trustStats.map(stat => (
                <div key={stat.label} className="fixer-trust-cell">
                  <FixerKicker channel="signal" active={false}>{stat.label}</FixerKicker>
                  <span className="fixer-subheading">{stat.value}</span>
                </div>
              ))}
            </div>
          </FixerSection>

          <FixerSection id="tjenester">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="matrix"
                kicker={c.servicesIntro.number}
                title={c.servicesIntro.title}
                copy={c.servicesIntro.copy}
                titleId="agency-services-heading"
              />
              <div className="fixer-card-grid-2" role="list">
                {c.services.map(service => (
                  <FixerCard key={service.title} interactive role="listitem">
                    <div className="fixer-stack-md">
                      <div className="flex items-center gap-2">
                        <FutureDotMatrixIcon glyph="matrix" state="signal" size={14} />
                        {service.href ? (
                          <a href={service.href} className="fixer-subheading no-underline hover:underline">
                            {service.title}
                          </a>
                        ) : (
                          <h3 className="fixer-subheading">{service.title}</h3>
                        )}
                      </div>
                      <p className="fixer-body-sm">{service.description}</p>
                      <FixerBulletList items={service.outcomes} />
                    </div>
                  </FixerCard>
                ))}
              </div>
            </div>
          </FixerSection>

          <FixerSection id="bransjer">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="screen"
                kicker={c.industriesIntro.number}
                title={c.industriesIntro.title}
                copy={c.industriesIntro.copy}
                titleId="agency-industries-heading"
              />
              <div className="fixer-card-grid-4" role="list">
                {c.industries.map(industry => (
                  <FixerCard key={industry.title} interactive role="listitem">
                    <div className="fixer-stack-md">
                      <h3 className="fixer-subheading">{industry.title}</h3>
                      <p className="fixer-body-sm">{industry.description}</p>
                      <FixerBulletList items={industry.useCases} />
                    </div>
                  </FixerCard>
                ))}
              </div>
            </div>
          </FixerSection>

          <FixerSection id="agenter">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="report"
                kicker={c.agentsIntro.number}
                title={c.agentsIntro.title}
                copy={c.agentsIntro.copy}
                titleId="agency-agents-heading"
              />
              <div className="fixer-stack-lg" role="list">
                {c.agentPlatforms.map(platform => (
                  <FixerCard key={platform.name} className="agency-agent-card" interactive role="listitem">
                    <div className="fixer-stack-md">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="fixer-stack-sm">
                          <FixerKicker channel="signal" active={false}>{platform.name}</FixerKicker>
                          <h3 className="fixer-subheading">{platform.tagline}</h3>
                        </div>
                        <a
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fixer-body-sm text-[var(--fc-accent)] no-underline hover:underline"
                        >
                          Dokumentasjon ↗
                        </a>
                      </div>
                      <p className="fixer-body-sm max-w-3xl">{platform.description}</p>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="fixer-stack-sm">
                          <FixerKicker channel="memory" active={false}>{c.platformLabels.deploy}</FixerKicker>
                          <p className="fixer-body-sm">{platform.deploy}</p>
                        </div>
                        <div className="fixer-stack-sm">
                          <FixerKicker channel="memory" active={false}>{c.platformLabels.bestFor}</FixerKicker>
                          <FixerBulletList items={platform.bestFor} />
                        </div>
                        <div className="fixer-stack-sm">
                          <FixerKicker channel="memory" active={false}>{c.platformLabels.features}</FixerKicker>
                          <FixerBulletList items={platform.features} />
                        </div>
                      </div>
                    </div>
                  </FixerCard>
                ))}
              </div>
              <div className="fixer-stack-md text-center">
                <p className="fixer-body-sm max-w-xl mx-auto">{c.agentsCta.copy}</p>
                <a href={bookingUrl} target="_blank" rel="noreferrer" className="fixer-btn-primary mx-auto">
                  {c.agentsCta.label}
                </a>
              </div>
            </div>
          </FixerSection>

          <FixerSection id="egen-fbc-losning">
            <FixerSectionIntro
              channel="report"
              kicker={c.forkSection.number}
              title={c.forkSection.title}
              copy={c.forkSection.copy}
              titleId="agency-fork-heading"
            />
          </FixerSection>

          <FixerSection id="prosess">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="vision"
                kicker={c.processIntro.number}
                title={c.processIntro.title}
                titleId="agency-process-heading"
              />
              <div className="fixer-process-grid" role="list">
                {c.processSteps.map(step => (
                  <FixerCard key={step.step} className="flex flex-col gap-3" interactive role="listitem">
                    <span className="fixer-step-badge">{step.step}</span>
                    <h3 className="fixer-subheading">{step.title}</h3>
                    <p className="fixer-body-sm">{step.body}</p>
                  </FixerCard>
                ))}
              </div>
            </div>
          </FixerSection>

          <AiAgencyFaqSection />
          <AiAgencyContactSection year={year} onStartChat={onStartChat} />
        </main>
      </div>
    </div>
  )
}

export default AiAgencyPage
